'use client';

import { motion } from 'motion/react';
import Image from '@/components/Image';
import { FaChrome } from 'react-icons/fa6';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

/* ── Mini simulation visuals ── */
function SimVisual({ type }: { type: 'gravity' | 'circuit' | 'projectile' | 'waves' }) {
  if (type === 'gravity') {
    return (
      <div className="flex flex-col items-center gap-1">
        <div className="flex items-center gap-3">
          <div className="h-6 w-6 rounded-full bg-[#E8740C]/60" />
          <div className="flex flex-col items-center">
            <div className="h-px w-10 bg-[#8A96A8]/30" />
            <span className="text-[8px] text-[#8A96A8]">F = ?</span>
          </div>
          <div className="h-4 w-4 rounded-full bg-[#4ECDC4]/60" />
        </div>
        <div className="rounded bg-[#E8740C]/10 px-2 py-0.5 text-[9px] text-[#E8740C]">
          what happens when closer?
        </div>
      </div>
    );
  }
  if (type === 'circuit') {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <div className="h-4 w-6 rounded-sm border border-[#E8740C]/40 bg-[#E8740C]/10 text-center text-[7px] leading-[16px] text-[#E8740C]">+</div>
          <div className="h-px w-6 bg-[#E8740C]/40" />
          <div className="h-3 w-3 rounded-full border border-[#FFBF00]/60 bg-[#FFBF00]/20" />
          <div className="h-px w-6 bg-[#E8740C]/40" />
          <div className="h-4 w-6 rounded-sm border border-[#E8740C]/40 bg-[#E8740C]/10 text-center text-[7px] leading-[16px] text-[#E8740C]">-</div>
        </div>
        <div className="rounded bg-[#FFBF00]/10 px-2 py-0.5 text-center text-[9px] text-[#CC9900]">
          bulb won&apos;t light!
        </div>
      </div>
    );
  }
  if (type === 'projectile') {
    return (
      <div className="relative h-12 w-full">
        <svg viewBox="0 0 120 48" className="h-full w-full">
          <path d="M10 40 Q60 -10 110 40" fill="none" stroke="#E8740C" strokeWidth="2" strokeDasharray="4 2" opacity="0.5" />
          <circle cx="10" cy="40" r="4" fill="#E8740C" opacity="0.7" />
          <circle cx="60" cy="5" r="3" fill="#E8740C" opacity="0.4" />
          <text x="55" y="20" fontSize="7" fill="#8A96A8" textAnchor="middle">angle?</text>
        </svg>
      </div>
    );
  }
  // waves
  return (
    <div className="flex flex-col items-center gap-1">
      <svg viewBox="0 0 120 24" className="h-6 w-full">
        <path d="M0 12 Q15 0 30 12 Q45 24 60 12 Q75 0 90 12 Q105 24 120 12" fill="none" stroke="#4ECDC4" strokeWidth="2" opacity="0.6" />
        <path d="M0 12 Q15 4 30 12 Q45 20 60 12 Q75 4 90 12 Q105 20 120 12" fill="none" stroke="#E8740C" strokeWidth="1.5" strokeDasharray="3 2" opacity="0.4" />
      </svg>
      <div className="flex gap-1">
        <div className="rounded bg-[#4ECDC4]/15 px-1.5 py-0.5 text-[8px] text-[#4ECDC4]">frequency</div>
        <div className="rounded bg-[#E8740C]/15 px-1.5 py-0.5 text-[8px] text-[#E8740C]">amplitude</div>
      </div>
    </div>
  );
}

/* ── Pain points on PhET ── */
const PAIN_POINTS: {
  problem: string;
  context: string;
  pengiResponse: string;
  simType: 'gravity' | 'circuit' | 'projectile' | 'waves';
}[] = [
  {
    problem: '"I moved the slider but nothing makes sense"',
    context: 'Changed mass in Gravity Force Lab but doesn\'t connect it to the formula.',
    pengiResponse: '"You doubled the mass. Look at the force arrow — did it get bigger or smaller? What does that tell you about the relationship?"',
    simType: 'gravity',
  },
  {
    problem: '"The bulb won\'t light up!"',
    context: 'Built a circuit in Circuit Construction Kit but left a gap or used wrong components.',
    pengiResponse: '"Trace the path from the battery\'s + terminal. Does the current have a complete loop back to the − terminal?"',
    simType: 'circuit',
  },
  {
    problem: '"How do I hit the target?"',
    context: 'Projectile Motion sim — trial-and-error without understanding angle vs velocity trade-off.',
    pengiResponse: '"You\'re shooting at 80°. What happens to the horizontal distance if you lower the angle? Try 45° and watch."',
    simType: 'projectile',
  },
  {
    problem: '"What am I supposed to learn from this?"',
    context: 'Teacher assigned a PhET sim but the student doesn\'t know what to observe or explore.',
    pengiResponse: '"This sim shows wave interference. Try changing the frequency of one source. What happens where the two waves meet?"',
    simType: 'waves',
  },
];

/* ── Onboarding ── */
const ONBOARDING_STEPS = [
  {
    step: '1',
    title: 'Install & sign in',
    subtitle: '30 seconds.',
    body: 'Add the Pengi extension from the Chrome Web Store and sign in with Google or email.',
    detail: 'Works on Chrome, Edge, Brave. Parents can create managed student accounts.',
  },
  {
    step: '2',
    title: 'Open any PhET simulation',
    subtitle: 'Pengi appears automatically.',
    body: 'Go to phet.colorado.edu and open any HTML5 sim. A Pengi button appears in the top-right corner.',
    detail: 'Pengi tracks slider changes, object positions, and simulation state in real time.',
  },
  {
    step: '3',
    title: 'Explore with guidance',
    subtitle: 'Voice or text.',
    body: '"Why did the force change?" "What should I try next?" — Pengi sees what you\'re doing and responds.',
    detail: 'Socratic method: guides discovery, never gives away the answer.',
  },
];

/* ── Synergy ── */
const SYNERGY_POINTS = [
  {
    title: 'During lab assignments',
    description: 'Teacher assigns a PhET sim but can\'t sit with every student. Pengi fills that gap — asking the questions a good teacher would.',
    icon: '🔬',
  },
  {
    title: 'Connecting sim to theory',
    description: 'Students play with sliders but miss the physics. Pengi bridges the gap: "You changed X. What happened to Y? What law explains that?"',
    icon: '📐',
  },
  {
    title: 'Building scientific thinking',
    description: 'Instead of "the answer is 9.8 m/s²", Pengi asks "what would happen on the Moon?" — teaching students to think like scientists.',
    icon: '🧪',
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

interface PhetPageClientProps {
  chromeStoreUrl: string;
}

export default function PhetPageClient({ chromeStoreUrl }: PhetPageClientProps) {
  return (
    <>
      {/* ══════ PAIN POINTS ══════ */}
      <section
        className="py-14 md:py-20"
        style={{
          background:
            'linear-gradient(180deg, #FAFCFF 0%, #F4F7FB 30%, #F4F7FB 70%, #FAFCFF 100%)',
        }}
      >
        <div className="mx-auto max-w-[1000px] px-4 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-4 flex items-center gap-3">
              <span className="display-text-semi text-xs uppercase tracking-[0.18em] text-[#8A96A8]">
                Real Problems
              </span>
              <div className="h-px flex-1 bg-[#E3E8F2]" />
            </div>
            <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <h2 className="emphasize-text max-w-[480px] text-[28px] leading-[1.1] tracking-[-0.03em] text-[#0D1929] md:text-[40px]">
                Sound familiar?
              </h2>
              <p className="display-text max-w-[320px] text-sm leading-[1.8] text-[#4A5A72] md:text-right">
                PhET sims are powerful. But without guidance, students just move sliders randomly.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
          >
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              breakpoints={{
                0: { slidesPerView: 1.08 },
                640: { slidesPerView: 1.5 },
                900: { slidesPerView: 2 },
              }}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3800, disableOnInteraction: false, pauseOnMouseEnter: true }}
              loop
              style={
                {
                  '--swiper-pagination-color': '#E8740C',
                  '--swiper-pagination-bullet-inactive-color': '#D1D9E6',
                  '--swiper-pagination-bullet-inactive-opacity': '1',
                  paddingBottom: '44px',
                } as React.CSSProperties
              }
            >
              {PAIN_POINTS.map((item) => (
                <SwiperSlide key={item.problem}>
                  <div className="group h-full rounded-[20px] border border-[#E3E8F2] bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
                    <div className="flex gap-4">
                      <div className="hidden shrink-0 sm:block">
                        <div className="w-[130px] overflow-hidden rounded-xl bg-gradient-to-br from-[#1a1a2e] to-[#16213e] p-3">
                          <SimVisual type={item.simType} />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="emphasize-text text-base text-[#0D1929]">{item.problem}</p>
                        <p className="display-text mt-1 text-sm leading-[1.6] text-[#8A96A8]">{item.context}</p>
                        <div className="mt-3 rounded-xl bg-[#F0FBE8] p-3 transition-all duration-200 group-hover:bg-[#E5F7DA]">
                          <div className="mb-1 flex items-center gap-1.5">
                            <Image src="/assets/landing-logo.png" alt="Pengi" width={16} height={16} optimizedWidth={32} />
                            <span className="display-text-semi text-[11px] text-[#277A1C]">Pengi:</span>
                          </div>
                          <p className="display-text text-[13px] leading-[1.6] text-[#277A1C] italic">
                            {item.pengiResponse}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </div>
      </section>

      {/* ══════ STATS + BEFORE/AFTER ══════ */}
      <section className="bg-[#FAFCFF] py-14 md:py-20">
        <div className="mx-auto max-w-[1000px] px-4 md:px-8">
          <motion.div
            className="mb-16 grid gap-5 sm:grid-cols-3"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={staggerContainer}
          >
            {[
              { number: '4.2x', label: 'deeper concept retention', sub: 'vs reading textbook alone' },
              { number: '< 3 min', label: 'from confused to exploring', sub: 'with guided questions' },
              { number: '91%', label: 'complete their PhET lab', sub: 'instead of random clicking' },
            ].map((stat) => (
              <motion.div
                key={stat.label}
                variants={fadeInUp}
                transition={{ duration: 0.4 }}
                className="rounded-[20px] border border-[#E3E8F2] bg-white p-6 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(0,0,0,0.04)]"
              >
                <p className="emphasize-text text-[36px] tracking-[-0.03em] text-[#E8740C] md:text-[44px]">{stat.number}</p>
                <p className="emphasize-text mt-1 text-sm text-[#0D1929]">{stat.label}</p>
                <p className="display-text mt-1 text-xs text-[#8A96A8]">{stat.sub}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-4 flex items-center gap-3">
              <span className="display-text-semi text-xs uppercase tracking-[0.18em] text-[#8A96A8]">The Difference</span>
              <div className="h-px flex-1 bg-[#E3E8F2]" />
            </div>
            <h2 className="emphasize-text mb-10 max-w-[500px] text-[28px] leading-[1.1] tracking-[-0.03em] text-[#0D1929] md:text-[36px]">
              Without Pengi vs. with Pengi
            </h2>
          </motion.div>

          <motion.div
            className="grid gap-5 md:grid-cols-2"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} transition={{ duration: 0.4 }} className="rounded-[20px] border border-[#E3E8F2] bg-white p-7">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#FEE2E2] px-3 py-1">
                <div className="h-2 w-2 rounded-full bg-[#EF4444]" />
                <span className="display-text-semi text-xs text-[#B91C1C]">Without Pengi</span>
              </div>
              <div className="space-y-4">
                {[
                  { label: 'Opens PhET sim', bar: 50, time: 'Moves sliders randomly' },
                  { label: 'No idea what to observe', bar: 85, time: 'Skips the learning goal' },
                  { label: 'Fills in worksheet', bar: 70, time: 'Guesses or Googles answers' },
                  { label: 'Concept retention', bar: 15, time: 'Forgot it by next week' },
                ].map((row) => (
                  <div key={row.label}>
                    <div className="flex items-baseline justify-between">
                      <span className="display-text-semi text-[13px] text-[#0D1929]">{row.label}</span>
                      <span className="display-text text-[11px] text-[#8A96A8]">{row.time}</span>
                    </div>
                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-[#F4F7FB]">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#FCA5A5] to-[#EF4444]" style={{ width: `${row.bar}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-xl bg-[#FEF2F2] p-4 text-center">
                <p className="emphasize-text text-[13px] text-[#B91C1C]">Result: simulation = fancy toy, no learning</p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} transition={{ duration: 0.4 }} className="rounded-[20px] border border-[#C5EDB8] bg-white p-7 shadow-[0_4px_24px_rgba(66,197,45,0.06)]">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#F0FBE8] px-3 py-1">
                <div className="h-2 w-2 rounded-full bg-[#42C52D]" />
                <span className="display-text-semi text-xs text-[#277A1C]">With Pengi</span>
              </div>
              <div className="space-y-4">
                {[
                  { label: 'Opens PhET sim', bar: 50, time: 'Pengi asks "what do you expect?"' },
                  { label: 'Guided exploration', bar: 90, time: '"Try changing mass. What happened?"' },
                  { label: 'Connects to theory', bar: 85, time: '"That\'s the inverse-square law!"' },
                  { label: 'Concept retention', bar: 90, time: 'Can explain it in their own words' },
                ].map((row) => (
                  <div key={row.label}>
                    <div className="flex items-baseline justify-between">
                      <span className="display-text-semi text-[13px] text-[#0D1929]">{row.label}</span>
                      <span className="display-text text-[11px] text-[#8A96A8]">{row.time}</span>
                    </div>
                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-[#F4F7FB]">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#86EFAC] to-[#42C52D]" style={{ width: `${row.bar}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-xl bg-[#F0FBE8] p-4 text-center">
                <p className="emphasize-text text-[13px] text-[#277A1C]">Result: simulation = real lab experience</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ══════ ONBOARDING ══════ */}
      <section
        className="py-14 md:py-20"
        style={{ background: 'linear-gradient(180deg, #FAFCFF 0%, #F4F7FB 30%, #F4F7FB 70%, #FAFCFF 100%)' }}
      >
        <div className="mx-auto max-w-[900px] px-4 md:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeInUp} transition={{ duration: 0.5 }}>
            <div className="mb-4 flex items-center gap-3">
              <span className="display-text-semi text-xs uppercase tracking-[0.18em] text-[#8A96A8]">Get Started</span>
              <div className="h-px flex-1 bg-[#E3E8F2]" />
            </div>
            <h2 className="emphasize-text mb-12 max-w-[500px] text-[28px] leading-[1.1] tracking-[-0.03em] text-[#0D1929] md:text-[40px]">
              From install to &ldquo;aha!&rdquo;
              <br />in 30 seconds
            </h2>
          </motion.div>

          <motion.div className="relative" initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} variants={staggerContainer}>
            <div className="absolute left-[27px] top-[48px] hidden h-[calc(100%-96px)] w-0.5 md:block" style={{ background: 'linear-gradient(to bottom, #E8740C, #E8740C 60%, transparent)' }} />
            <div className="space-y-5">
              {ONBOARDING_STEPS.map((item) => (
                <motion.div key={item.step} variants={fadeInUp} transition={{ duration: 0.4 }} className="flex gap-5 md:gap-6">
                  <div className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-xl shadow-md" style={{ background: 'linear-gradient(135deg, #E8740C 0%, #CC5500 100%)', boxShadow: '0 4px 16px rgba(232,116,12,0.25)' }}>
                    <span className="font-extrabold text-white">{item.step}</span>
                  </div>
                  <div className="flex-1 rounded-[20px] border border-[#E3E8F2] bg-white p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#E8740C]/30 hover:shadow-[0_8px_28px_rgba(232,116,12,0.06)]">
                    <div className="flex flex-wrap items-baseline gap-x-3">
                      <h3 className="emphasize-text text-base text-[#0D1929]">{item.title}</h3>
                      <span className="display-text-semi text-xs text-[#E8740C]">{item.subtitle}</span>
                    </div>
                    <p className="display-text mt-2 text-sm leading-[1.7] text-[#4A5A72]">{item.body}</p>
                    <p className="display-text mt-2 text-xs text-[#B0B8C4]">{item.detail}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div className="mt-12 text-center" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }}>
            <a href={chromeStoreUrl} target="_blank" rel="noopener noreferrer" className="display-text-bold group inline-flex items-center gap-2.5 rounded-full bg-[#E8740C] px-8 py-3.5 text-base text-white shadow-[0_4px_24px_rgba(232,116,12,0.3)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#CC5500] hover:shadow-[0_8px_32px_rgba(232,116,12,0.4)]">
              <FaChrome className="h-5 w-5 transition-transform duration-300 group-hover:rotate-180" />
              Get Started
            </a>
          </motion.div>
        </div>
      </section>

      {/* ══════ AI + TEACHER SYNERGY ══════ */}
      <section className="bg-[#FAFCFF] py-14 md:py-20">
        <div className="mx-auto max-w-[1000px] px-4 md:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeInUp} transition={{ duration: 0.5 }}>
            <div className="mb-4 flex items-center gap-3">
              <span className="display-text-semi text-xs uppercase tracking-[0.18em] text-[#8A96A8]">Works With Your Classes</span>
              <div className="h-px flex-1 bg-[#E3E8F2]" />
            </div>
            <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <h2 className="emphasize-text max-w-[520px] text-[28px] leading-[1.1] tracking-[-0.03em] text-[#0D1929] md:text-[40px]">
                &ldquo;But my kid has a
                <br />science teacher&rdquo;
              </h2>
              <p className="display-text max-w-[280px] text-sm leading-[1.8] text-[#4A5A72] md:text-right">
                Great. Pengi is the lab assistant that&apos;s always available.
              </p>
            </div>
          </motion.div>

          <motion.div className="grid gap-5 md:grid-cols-3" initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} variants={staggerContainer}>
            {SYNERGY_POINTS.map((item) => (
              <motion.div key={item.title} variants={fadeInUp} transition={{ duration: 0.4 }} className="group rounded-[20px] border border-[#E3E8F2] bg-white p-7 transition-all duration-200 hover:-translate-y-1 hover:border-[#E8740C]/30 hover:shadow-[0_8px_32px_rgba(232,116,12,0.08)]">
                <div className="mb-4 text-2xl">{item.icon}</div>
                <h3 className="emphasize-text mb-3 text-base text-[#0D1929]">{item.title}</h3>
                <p className="display-text text-sm leading-[1.7] text-[#566070]">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div className="mt-8 rounded-[20px] p-7 text-center" style={{ background: 'linear-gradient(135deg, #FEF3E8 0%, #F0FBE8 100%)', border: '1px solid #E8DFD4' }} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}>
            <p className="emphasize-text text-base text-[#0D1929]">
              Every PhET sim becomes a guided lab experiment —
            </p>
            <p className="display-text mt-1.5 text-sm text-[#6B7A90]">
              not just a toy to click around in.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}
